import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { format, getDate } from 'date-fns';
import { connect, FormikContextType } from 'formik';
import { useTranslation } from 'react-i18next';

import { VERTICAL_SEPARATOR } from '~/Constants/Colors.constants';
import { MenuItem, Select } from '~/UIKit';
import { SelectChangeEvent } from '@mui/material';
import { ExtendedEventSeries } from '../../../extendedEventSeries.model';
import { makeStyles } from '~/UIKit/Style';
import { TIME_FREQUENCY } from '~/Models/DTO/EventSeries.model';

export interface EventScheduleTimeSeriesMonthlyInputProps {
  startDate: string;
}

const useStyles = makeStyles(() => ({
  EventScheduleTimeSeriesMonthlyInputContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: 15,
    paddingLeft: 15,
    height: 50,
    alignItems: 'center',
    borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
  },
}));

export const EventScheduleTimeSeriesMonthlyInput = connect<
  EventScheduleTimeSeriesMonthlyInputProps,
  ExtendedEventSeries
>(
  ({
    formik,
    startDate,
  }: EventScheduleTimeSeriesMonthlyInputProps & {
    formik: FormikContextType<ExtendedEventSeries>;
  }) => {
    const { t } = useTranslation();
    const date = useMemo(() => new Date(startDate), [startDate]);
    const { classes } = useStyles();
    const [selected, setSelected] = useState(() => (formik.values.bySetPos && 1) ?? 0);
    const weekday = useMemo(() => format(date, 'eeee').toLowerCase(), [date]);
    const day = useMemo(() => getDate(date), [date]);
    const weekNumber = useMemo(() => {
      const index = Math.floor((day - 1) / 7);
      return [
        { label: t('app.first'), value: 1 },
        { label: t('app.second'), value: 2 },
        { label: t('app.third'), value: 3 },
        { label: t('app.fourth'), value: -2 },
        { label: t('app.last'), value: -1 },
      ][index];
    }, [t, day]);
    const options = useMemo(
      () => [
        { label: t('time.frequency.monthly.day', { day }) },
        {
          label: t('time.frequency.monthly.weekday', {
            weekNumber: weekNumber.label,
            weekday: t(`app.weekdays.${weekday}`),
          }),
        },
      ],
      [date, weekNumber, day, t],
    );
    const onSelectChange = useCallback(
      (event: SelectChangeEvent<number>) => {
        event.preventDefault();
        setSelected(Number(event.target.value));
      },
      [setSelected],
    );

    useEffect(() => {
      setTimeout(() => {
        formik.setValues((prev) => ({
          ...prev,
          frequency: TIME_FREQUENCY.MONTHLY,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
          ...(selected === 0
            ? {
                byMonthDay: day,
                bySetPos: null,
              }
            : {
                byMonthDay: null,
                bySetPos: weekNumber.value,
                [weekday]: true,
              }),
        }));
      }, 0);
    }, [startDate, selected, day, weekNumber, weekday]);

    return (
      <div
        className={classes.EventScheduleTimeSeriesMonthlyInputContainer}
        data-testid="EventScheduleTimeSeriesMonthlyInput"
      >
        <Select newDesign onChange={onSelectChange} value={selected}>
          {options.map((option, index) => (
            <MenuItem key={option.label} value={index}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
    );
  },
);
